import React from 'react'

import { Flex } from 'antd'
import { twMerge } from 'tailwind-merge'

import { EnvironmentLogoText } from 'src/constants'

import { useTheme } from '../../hooks/use-theme'

export enum LogoColor {
  White = 'White',
  Black = 'Black',
  Blue = 'Blue',
}

type Props = {
  className?: string
  color: LogoColor
  height?: React.SVGAttributes<SVGElement>['height']
  showText?: boolean
  width?: React.SVGAttributes<SVGElement>['width']
}

export const Logo = ({
  className,
  color: logoColor,
  height = 22,
  showText = true,
  width = 148,
}: Props) => {
  const { getTokenVal } = useTheme()
  const color =
    logoColor === LogoColor.Black
      ? 'oklch(14.5% 0 0)'
      : logoColor === LogoColor.Blue
        ? getTokenVal('colorMenuDarkBg')
        : 'oklch(98.5% 0 0)'

  const logoSvg = (
    <svg
      width={width}
      height={height}
      viewBox={showText ? '0 0 1369 208' : '0 0 280 204'}
      fill="none"
      overflow="hidden"
      xmlns="http://www.w3.org/2000/svg"
    >
      {showText ? (
        <>
          <path
            d="M46.2312 193.755C31.4862 184.867 20.0778 172.569 12.0496 156.877C4.00683 141.185 0 123.395 0 103.55C0 83.7058 4.06508 65.9156 12.1953 50.2089C20.3254 34.5168 31.865 22.2196 46.8141 13.3317C61.7485 4.4439 79.0579 0 98.7277 0C119.913 0 137.499 4.82273 151.486 14.4682C165.488 24.1137 175.79 37.256 182.404 53.8952H146.372C142.205 45.765 136.202 39.3395 128.364 34.6042C120.51 29.8835 110.632 27.5085 98.7131 27.5085C84.7112 27.5085 72.749 31.0054 62.8267 37.9991C52.9044 44.9928 45.3862 54.274 40.2721 65.799C35.1579 77.3386 32.6081 89.9127 32.6081 103.536C32.6081 117.159 35.1579 129.733 40.2721 141.273C45.3716 152.812 52.8898 162.035 62.8267 168.927C72.749 175.833 84.7112 179.286 98.7131 179.286C110.063 179.286 119.855 177.013 128.072 172.482C136.29 167.951 142.686 161.511 147.217 153.191C151.763 144.872 154.211 135.139 154.59 123.978H97.2853V96.4547H185.799V202.846H161.117L158.276 180.714C152.215 188.655 144.128 195.037 134.017 199.859C123.89 204.682 111.753 207.086 97.5621 207.086C78.0818 207.086 60.9618 202.642 46.2167 193.755H46.2312Z"
            fill={color}
          />
          <path
            d="M240.13 196.887C228.591 190.082 219.659 180.714 213.321 168.795C206.983 156.877 203.821 143.647 203.821 129.077C203.821 114.507 206.983 101.277 213.321 89.3584C219.659 77.44 228.591 68.0859 240.13 61.2671C251.67 54.4628 265.002 51.0533 280.14 51.0533C295.279 51.0533 308.363 54.4628 320.004 61.2671C331.646 68.0714 340.621 77.44 346.959 89.3584C353.297 101.277 356.459 114.507 356.459 129.077C356.459 143.647 353.297 156.877 346.959 168.795C340.621 180.714 331.631 190.068 320.004 196.887C308.377 203.705 295.089 207.1 280.14 207.1C265.191 207.1 251.67 203.691 240.13 196.887ZM304.822 174.332C311.626 169.509 316.741 163.084 320.135 155.041C323.545 146.998 325.235 138.358 325.235 129.077C325.235 119.796 323.53 111.155 320.135 103.113C316.726 95.07 311.626 88.6008 304.822 83.6761C298.018 78.7659 289.786 76.3035 280.14 76.3035C270.495 76.3035 262.263 78.7659 255.458 83.6761C248.654 88.6008 243.54 95.07 240.13 103.113C236.721 111.155 235.031 119.81 235.031 129.077C235.031 138.343 236.735 146.998 240.13 155.041C243.54 163.084 248.639 169.509 255.458 174.332C262.263 179.155 270.495 181.559 280.14 181.559C289.786 181.559 298.018 179.155 304.822 174.332Z"
            fill={color}
          />
          <path
            d="M351.74 55.3237H384.93L407.339 116.038C411.681 127.956 417.174 145.455 423.789 168.52H424.926C430.783 147.524 436.363 130.04 441.667 116.038L463.799 55.3237H497.281L439.977 202.847H408.767L351.754 55.3237H351.74Z"
            fill={color}
          />
          <path
            d="M492.707 4.25519H525.329L552.561 104.396C555.213 114.231 557.573 123.731 559.657 132.91C561.741 142.089 563.635 151.298 565.325 160.564H566.461C570.249 140.137 574.591 121.327 579.516 104.105L607.607 4.2406H640.23L668.322 104.105C673.232 121.123 677.676 139.948 681.653 160.564H682.79C684.669 150.54 686.578 141.04 688.457 132.05C690.337 123.075 692.61 113.852 695.262 104.396L722.494 4.25519H755.116L698.38 202.832H665.757L636.544 99.2821C632.377 84.537 628.414 67.0382 624.625 46.8002H623.489C620.08 65.1441 616.014 82.6429 611.294 99.2821L582.081 202.832H549.458L492.721 4.25519H492.707Z"
            fill={color}
          />
          <path
            d="M774.407 196.887C763.057 190.082 754.271 180.772 748.02 168.941C741.784 157.124 738.651 143.836 738.651 129.077C738.651 114.317 741.769 101.277 748.02 89.3584C754.271 77.44 763.057 68.0859 774.407 61.2671C785.757 54.4628 798.71 51.0533 813.28 51.0533C830.677 51.0533 845.101 55.2642 856.539 63.6857C867.977 72.1073 876.15 83.5012 881.075 97.8674C882.955 103.36 884.295 109.174 885.038 115.308C885.796 121.457 886.175 128.407 886.175 136.158V140.704H770.997C771.376 147.698 773.314 154.414 776.811 160.84C780.307 167.265 785.218 172.525 791.556 176.59C797.894 180.655 805.412 182.695 814.111 182.695C823.945 182.695 831.784 180.524 837.656 176.168C843.513 171.826 847.491 166.712 849.574 160.854H883.333C878.977 174.478 870.847 185.58 858.943 194.191C847.024 202.802 832.09 207.1 814.125 207.1C798.986 207.1 785.757 203.691 774.407 196.887ZM848.875 96.0316C845.757 89.8829 841.167 84.9291 835.121 81.1409C829.059 77.3526 821.789 75.473 813.28 75.473C804.771 75.473 798.01 77.3672 791.862 81.1409C785.713 84.9291 780.89 89.8829 777.393 96.0316C773.897 102.18 771.769 108.941 771.012 116.313H854.135C853.756 108.941 852.008 102.18 848.89 96.0316H848.875Z"
            fill={color}
          />
          <path d="M904.824 4.25549H936.034V202.833H904.824V4.25549Z" fill={color} />
          <path d="M962.493 4.25549H993.702V202.833H962.493V4.25549Z" fill={color} />
          <path d="M1185.03 108.566H1088.62V205.11H1185.03V108.566Z" fill={color} />
          <path
            d="M1368.2 108.566C1307.14 73.8081 1246.08 143.323 1185.03 108.566V12.0214C1246.08 46.779 1307.14 -22.7362 1368.2 12.0214C1368.2 44.2029 1368.2 76.3843 1368.2 108.566Z"
            fill={color}
          />
        </>
      ) : (
        <>
          <path d="M96.4067 106.578H0V203.122H96.4067V106.578Z" fill={color} />
          <path
            d="M279.576 106.578C218.522 71.8203 157.461 141.336 96.4071 106.578V10.0337C157.461 44.7913 218.522 -24.7239 279.576 10.0337C279.576 42.2151 279.576 74.3965 279.576 106.578Z"
            fill={color}
          />
        </>
      )}
    </svg>
  )
  if (!EnvironmentLogoText || !showText) {
    return logoSvg
  }
  const isLarge = typeof width === 'number' && width > 200
  const badgeClassName = isLarge
    ? 'absolute top-1/2 left-1/2 translate-x-[160px] translate-y-[4px] text-[36px] font-bold text-orange-500'
    : 'absolute top-1/2 left-1/2 translate-x-[60px] translate-y-[1px] text-[14px] font-bold text-orange-500'
  return (
    <Flex gap="10px" align="center" className={twMerge('relative w-fit', className)}>
      {logoSvg}
      <span className={badgeClassName}>{EnvironmentLogoText}</span>
    </Flex>
  )
}
